import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { ensureExists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCreateBrandGroupMutation } from '../hooks/useCreateBrandGroupMutation'
import { useUpdateBrandGroupMutation } from '../hooks/useUpdateBrandGroupMutation'

const schema = z.object({ name: z.string().min(1) })
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const EditBrandGroupDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { brandGroup, dialogType } = ensureExists(
    dialogInfo,
    t('assertion_failed'),
  )

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: { name: brandGroup?.name ?? '' },
    resolver,
  })
  const nameField = useFormField(control, 'name')

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const logEvent = useLogEvent()
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const createBrandGroupMutation = useCreateBrandGroupMutation({
    onSuccess: () => {
      logEvent('Add brand group')
      closeDialogWithRefresh()
    },
  })
  const updateBrandGroupMutation = useUpdateBrandGroupMutation({
    onSuccess: closeDialogWithRefresh,
  })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ name }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      if (dialogType === 'create') {
        createBrandGroupMutation.mutate(name)
      } else {
        updateBrandGroupMutation.mutate({ brandGroupId: brandGroup.id, name })
      }
    },
    [
      brandGroup,
      createBrandGroupMutation,
      dialogType,
      isDirty,
      resetDialogInfo,
      updateBrandGroupMutation,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={
        createBrandGroupMutation.isPending || updateBrandGroupMutation.isPending
      }
      mode={dialogType === 'create' ? 'create' : 'update'}
      title={
        dialogType === 'create' ? t('add_brand_group') : t('update_brand_group')
      }
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer label={t('brand_group_name')}>
        <StyledInput autoFocus={true} width="100%" {...nameField} />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
